import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getAvailableTemplates, getTemplatesForScan, deleteTemplate, cloneTemplate, normalizeTemplateId,
} from "../../services/templateService";
import { api } from "../lib/api";

// Report templates.
//
// templateService is the one real service layer in the codebase, so this hook wraps it
// rather than re-issuing its fetches. The dedupe below is not tidying: the endpoint
// returns the same template more than once when a tenant has cloned a default, and
// without it the grid shows visible duplicates.
//
// Pass the scan's file path when picking a template FOR a scan: the backend then returns
// only templates built for that scan's scanner major version (v1 templates for 1.x scans,
// v2 templates for 2.x scans), and reports how many it left out.
export function useTemplates(scanFileUrl = null) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPro, setIsPro] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [scanSchemaVersion, setScanSchemaVersion] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, sub] = await Promise.all([
        scanFileUrl
          ? getTemplatesForScan(scanFileUrl)
          : getAvailableTemplates().then((templates) => ({ templates, scanSchemaVersion: null, hiddenCount: 0 })),
        // Leading slash, deliberately. A relative path resolves against the CURRENT
        // route, so from a nested one like /report-builder/pick-template this asked for
        // /report-builder/s1/api/subscription — which the dev server answers with
        // index.html at status 200. The JSON parse then failed, the catch swallowed it,
        // and a Pro tenant was quietly told they were on community.
        api.get("/s1/api/subscription").catch(() => null),
      ]);
      const seen = new Set();
      setScanSchemaVersion(result.scanSchemaVersion);
      setHiddenCount(result.hiddenCount);
      setTemplates(
        (result.templates || []).filter((t) => {
          const key = t.name?.toLowerCase().trim();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
      );
      if (sub) setIsPro(!!sub.is_pro);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load templates.");
    } finally {
      setLoading(false);
    }
  }, [scanFileUrl]);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (t) => {
    setBusyId(t.id);
    try { await deleteTemplate(normalizeTemplateId(t)); await load(); }
    finally { setBusyId(null); }
  }, [load]);

  const clone = useCallback(async (t, newName) => {
    setBusyId(t.id);
    try { await cloneTemplate(normalizeTemplateId(t), newName); await load(); }
    finally { setBusyId(null); }
  }, [load]);

  // A template is the tenant's own when it is neither of the published defaults.
  //
  // "All" means everything this tenant can actually use, not everything that exists: a
  // community tenant's All excludes the pro defaults, and a pro tenant's excludes the
  // community ones. So All is always the union of the two tabs beside it, which is the
  // only reading under which the three counts add up.
  const groups = useMemo(() => {
    const yours = templates.filter((t) => !t.is_community_default && !t.is_pro_default);
    const community = templates.filter((t) => t.is_community_default);
    const pro = templates.filter((t) => t.is_pro_default);
    return {
      yours,
      community,
      pro,
      all: [...yours, ...(isPro ? pro : community)],
    };
  }, [templates, isPro]);

  return {
    templates, groups, loading, error, isPro, busyId, scanSchemaVersion, hiddenCount,
    reload: load, remove, clone,
  };
}
